import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { RefreshCw, ArrowLeft, Newspaper, Calendar, ExternalLink } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOPICS = [
  { id: "general", label: "All Politics" },
  { id: "alp", label: "Labor" },
  { id: "liberal", label: "Liberal" },
  { id: "greens", label: "Greens" },
  { id: "economy", label: "Economy" },
  { id: "environment", label: "Environment" },
  { id: "housing", label: "Housing" },
  { id: "healthcare", label: "Healthcare" },
];

const TOPIC_COLORS = {
  general: "#2E5948", alp: "#CC2529", liberal: "#1B4F9E",
  greens: "#10B981", economy: "#D97706", environment: "#059669",
  housing: "#7C3AED", healthcare: "#DB2777",
};

export default function NewsPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [topic, setTopic] = useState("general");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [cached, setCached] = useState(false);

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  const fetchNews = async (selectedTopic, forceRefresh = false) => {
    setLoading(true);
    setArticles([]);
    try {
      const r = await axios.post(`${API}/news/generate`, {
        topic: selectedTopic,
        force_refresh: forceRefresh,
      });
      setArticles(r.data.articles || []);
      setLastUpdated(r.data.last_updated);
      setCached(r.data.cached || false);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews(topic);
  }, [topic]);

  const topicColor = TOPIC_COLORS[topic] || "#2E5948";
  const topicLabel = TOPICS.find((t) => t.id === topic)?.label || "Politics";

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="news-page">
      {/* Hero */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1
                className="text-4xl sm:text-5xl font-black text-white mb-2"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                News Feed
              </h1>
              <p className="text-sm" style={{ color: "#94A3B8" }}>
                AI-generated political news briefs for early 2026.{" "}
                {cached && <span style={{ color: "#FFC82E" }}>Cached · </span>}
                {lastUpdated && (
                  <span>{new Date(lastUpdated).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                )}
              </p>
            </div>
            <button
              onClick={() => fetchNews(topic, true)}
              disabled={loading}
              data-testid="news-refresh-btn"
              className="flex items-center gap-2 px-4 py-2 font-bold text-sm border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
              style={{ backgroundColor: "#FFC82E", boxShadow: "2px 2px 0 #000" }}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Topic Tabs */}
        <div className="flex flex-wrap gap-1.5 mb-8">
          {TOPICS.map((t) => (
            <button
              key={t.id}
              data-testid={`news-topic-${t.id}`}
              onClick={() => setTopic(t.id)}
              className="px-3 py-1.5 text-xs font-bold border-2 border-black transition-all"
              style={{
                backgroundColor: topic === t.id ? TOPIC_COLORS[t.id] : surface,
                color: topic === t.id ? "#fff" : text,
                boxShadow: topic === t.id ? "2px 2px 0 #000" : "none",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="border-2 border-black p-5 animate-pulse"
                style={{ backgroundColor: surface, height: 120 }}
              />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div
            className="text-center py-20 border-2 border-black"
            style={{ backgroundColor: surface }}
          >
            <Newspaper className="w-10 h-10 mx-auto mb-3" style={{ color: textSec }} />
            <p className="font-black text-lg mb-1" style={{ color: text }}>
              No articles found
            </p>
            <p className="text-sm" style={{ color: textSec }}>
              Try refreshing or selecting a different topic.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article, i) => (
              <div
                key={i}
                className="border-2 border-black p-5 hover:-translate-y-0.5 transition-transform"
                style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }}
                data-testid={`news-article-${i}`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div
                    className="text-xs font-black px-2 py-0.5 border-2 border-black flex-shrink-0"
                    style={{ backgroundColor: topicColor, color: "#fff" }}
                  >
                    {topicLabel}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {article.date && (
                      <span className="flex items-center gap-1 text-xs font-medium" style={{ color: textSec }}>
                        <Calendar className="w-3 h-3" />
                        {article.date}
                      </span>
                    )}
                  </div>
                </div>
                <h3 className="font-black text-base mb-2 leading-snug" style={{ color: text }}>
                  {article.title}
                </h3>
                <p className="text-sm leading-relaxed mb-3" style={{ color: textSec }}>
                  {article.summary}
                </p>
                {article.source && (
                  <p className="text-xs font-bold" style={{ color: topicColor }}>
                    {article.source}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <div
          className="border-2 border-black p-4 mt-8 text-center"
          style={{ backgroundColor: isDark ? "#1E293B" : "#FFF8DC" }}
        >
          <p className="text-xs" style={{ color: textSec }}>
            News briefs are AI-generated and for educational purposes only. Always verify with
            trusted news sources like ABC News, The Guardian Australia, or The Australian.
          </p>
        </div>
      </div>
    </div>
  );
}
